import type { TFunction } from 'i18next'
import type { KeyMissing } from '@/ds'
import { dayMonthLong, formatMoneyShort, time } from '@/lib/format'
import { reportEvent } from '@/lib/monitoring'
import { deriveKeyMissing } from '@/services/accesos'
import type { KeyStatus } from '@/services/accesos'
import type { Deposit } from '@/services/fianza'
import type { PaymentSummary } from '@/services/pagos'
import type { Stay } from '@/services/reserva'

/** Rutas a las que lleva cada motivo (inventario D16). */
export const MISSING_ROUTES = { register: '/checkin', deposit: '/reserva/fianza/reservar', payment: '/reserva/pagos' } as const

/**
 * Llave bloqueada (PantallaAccesos.dc.html `locked`, inventario D6): lo que falta, cada motivo con enlace a
 * su paso. `time` no es una acción: va en la frase ("Se activará el 3 de octubre, 16:00 cuando completes:").
 * Si el cliente no encuentra ningún motivo y ya es la hora, `generic` es true y la pantalla muestra el
 * motivo genérico con el contacto; nunca uno inventado.
 */
export function lockedKeyContent(
  stay: Stay,
  key: KeyStatus,
  payment: PaymentSummary | null,
  deposit: Deposit | null,
  now: Date,
  lang: string,
  t: TFunction,
  go: (path: string) => void,
): { lockedText: string; items: KeyMissing[]; generic: boolean } {
  const missing = deriveKeyMissing(stay, payment, deposit, now).filter((m) => m.code !== 'time')
  const when = key.activeFrom ? `${dayMonthLong(key.activeFrom, lang)}, ${time(key.activeFrom, lang)}` : ''
  const items = missing.map((m): KeyMissing => {
    switch (m.code) {
      case 'register':
        return { label: t('v2.estancia.llave.falta.register', { count: m.count }), onClick: () => go(MISSING_ROUTES.register) }
      case 'deposit':
        return { label: t('v2.estancia.llave.falta.deposit'), onClick: () => go(MISSING_ROUTES.deposit) }
      default:
        return { label: t('v2.estancia.llave.falta.payment', { amount: formatMoneyShort(m.amount, lang) }), onClick: () => go(MISSING_ROUTES.payment) }
    }
  })
  const future = key.activeFrom !== null && now < new Date(key.activeFrom)
  if (items.length > 0) return { lockedText: t('v2.estancia.llave.seActivaraCuando', { when }), items, generic: false }
  if (future) return { lockedText: t('v2.estancia.llave.seActivara', { when }), items, generic: false }
  return { lockedText: t('v2.accesos.llave.sinMotivo'), items, generic: true }
}

const reported = new Set<string>()

/**
 * D6: si `showDomotic` activa la llave pero el cliente ve motivos pendientes (sin contar la hora), se muestra activa
 * (el PMS manda) y se registra la discrepancia en Sentry una vez por reserva y conjunto de motivos.
 */
export function reportKeyDiscrepancy(stay: Stay, key: KeyStatus, payment: PaymentSummary | null, deposit: Deposit | null, now: Date): boolean {
  if (key.state !== 'active') return false
  const codes = deriveKeyMissing(stay, payment, deposit, now)
    .filter((m) => m.code !== 'time')
    .map((m) => m.code)
  if (codes.length === 0) return false
  const id = `${stay.id}:${codes.join(',')}`
  if (reported.has(id)) return false
  reported.add(id)
  reportEvent('key_state_discrepancy', { missing: codes.join(',') })
  return true
}

/** Solo pruebas. */
export const __resetKeyDiscrepancies = () => reported.clear()
